import React, { useState } from 'react';
import { 
  Button, Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Select, MenuItem, InputLabel, FormControl, Alert, Fab, CircularProgress
} from '@mui/material';
import FeedbackIcon from '@mui/icons-material/Feedback';

// FIREBASE IMPORTS
import { getAuth } from "firebase/auth";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const FeedbackModal = () => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('feature');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
        setError("Please provide a title and description.");
        return;
    }

    setError(null);
    setMessage(null);
    setIsSubmitting(true);

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      // Allow anonymous feedback, but attach info if logged in
      const userEmail = user ? user.email : "Anonymous";
      const userId = user ? user.uid : "Anonymous";

      await addDoc(collection(db, "feedback"), {
        type,
        title,
        description,
        status: "new", // Sets it as "new" so it shows up properly in your Admin Dashboard
        user_email: userEmail,
        user_id: userId,
        created_at: serverTimestamp()
      });

      setMessage("Thanks! We've received your feedback.");
      
      // Close the modal and reset form after 2 seconds
      setTimeout(() => {
          setOpen(false);
          setMessage(null);
          setTitle("");
          setDescription("");
          setType("feature");
      }, 2000);

    } catch (err) {
      console.error(err);
      setError("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setMessage(null);
    setError(null);
    setTitle("");
    setDescription("");
    setType("feature");
  };

  return (
    <>
      {/* Floating Button in Bottom Right */}
      <Fab 
        color="secondary" 
        aria-label="feedback" 
        onClick={() => setOpen(true)}
        sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }} 
      >
        <FeedbackIcon />
      </Fab>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Submit Feedback</DialogTitle>
        <DialogContent>
          {message && <Alert severity="success" sx={{ mb: 2, mt: 1 }}>{message}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select value={type} label="Type" onChange={(e) => setType(e.target.value)}>
              <MenuItem value="feature">Feature Request 💡</MenuItem>
              <MenuItem value="bug">Bug Report 🐛</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            autoFocus
            margin="normal"
            label="Title"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Add Dark Mode"
          />
          
          <TextField
            margin="normal"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what you'd like to see..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={isSubmitting || !title.trim() || !description.trim()}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeedbackModal;